import React, { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { getPostBySlug, mediaUrl } from "../lib/api";
import { useContent } from "../context/ContentContext";
import SEO from "../seo/SEO";
import { SITE_URL, getSiteName, composeTitle } from "../seo/routeConfig";

const buildArticleJsonLd = (post, siteName) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}/blog/${post.slug}` },
  headline: post.title,
  description: post.excerpt || undefined,
  image: post.cover_image || undefined,
  datePublished: post.published_at || undefined,
  dateModified: post.updated_at || post.published_at || undefined,
  articleSection: post.category || undefined,
  keywords: (post.tags || []).join(", ") || undefined,
  author: {
    "@type": "Person",
    name: "Karen Boley",
    jobTitle: "Canadian Certified Counsellor",
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: siteName,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/og-image.jpg` },
  },
});

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const BlogPost = () => {
  const { slug } = useParams();
  const { content } = useContent();
  const blogEnabled = content.blogPage?.enabled !== false;
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!blogEnabled) return;
    setPost(null);
    setNotFound(false);
    getPostBySlug(slug)
      .then((data) => setPost(data))
      .catch(() => setNotFound(true));
  }, [slug, blogEnabled]);

  if (!blogEnabled) return <Navigate to="/" replace />;

  return (
    <div className="bg-cream min-h-screen">
      {post && (
        <SEO
          path={`/blog/${post.slug}`}
          title={composeTitle(post.seo_title || post.title, content)}
          description={post.meta_description || post.excerpt || `Read "${post.title}" on the ${getSiteName(content)} journal.`}
          ogImage={post.cover_image || undefined}
          ogType="article"
          article={{
            publishedTime: post.published_at,
            modifiedTime: post.updated_at,
            section: post.category,
            tags: post.tags,
          }}
          jsonLd={buildArticleJsonLd(post, getSiteName(content))}
        />
      )}
      {notFound && (
        <SEO path={`/blog/${slug}`} title={composeTitle("Post not found", content)} description="This story may have been moved or unpublished." noindex />
      )}
      <Navbar />
      <main className="pt-32 pb-20 min-h-[60vh]">
        <div className="mx-auto max-w-3xl px-5 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-semibold text-sage-dark hover:text-sage mb-8"><ArrowLeft className="h-4 w-4" /> All posts</Link>

          {notFound ? (
            <div className="py-20 text-center" data-testid="post-not-found">
              <h1 className="font-serif text-4xl text-ink">Post not found</h1>
              <p className="mt-3 text-ink-soft">This story may have been moved or unpublished.</p>
            </div>
          ) : !post ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-sage" /></div>
          ) : (
            <article data-testid="post-article">
              <div className="flex items-center gap-2 text-sm text-ink-muted">
                <span className="text-sage-dark font-semibold uppercase tracking-wide">{post.category}</span>
                <span>·</span>
                <span>{fmtDate(post.published_at)}</span>
              </div>
              <h1 className="mt-4 font-serif font-light text-4xl md:text-5xl tracking-tight text-ink leading-[1.1]">{post.title}</h1>
              {post.excerpt && <p className="mt-4 text-xl text-ink-soft leading-relaxed">{post.excerpt}</p>}

              {post.cover_image && (
                <div className="mt-8 rounded-[2rem] overflow-hidden border border-cream-2">
                  <img src={mediaUrl(post.cover_image)} alt={post.title} className="w-full object-cover" />
                </div>
              )}

              <div
                className="ql-editor-content mt-10 max-w-none text-ink-soft leading-relaxed [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:text-ink [&_h2]:mt-8 [&_h2]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:text-ink [&_h3]:mt-6 [&_h3]:mb-2 [&_p]:mb-4 [&_a]:text-sage-dark [&_a]:underline [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_blockquote]:border-l-4 [&_blockquote]:border-sage [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-ink [&_img]:rounded-xl [&_img]:my-6"
                dangerouslySetInnerHTML={{ __html: post.body_html }}
              />

              {post.tags?.length > 0 && (
                <div className="mt-10 flex flex-wrap gap-2">
                  {post.tags.map((t) => (
                    <span key={t} className="rounded-full bg-cream-2 px-3 py-1 text-sm text-ink-soft">#{t}</span>
                  ))}
                </div>
              )}
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPost;
